import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Box,
  Button,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CustomTableWithCollapse = ({
  columnsPadre,
  rowsPadre,
  columnsHijo,
  rowsHijo,
  filterFieldPadre,
  filterFieldHijo,
}) => {
  const [expandedRows, setExpandedRows] = useState([]);

  // Función para manejar el clic en expandir/colapsar
  const handleToggleExpand = (parentId) => {
    setExpandedRows((prevState) =>
      prevState.includes(parentId)
        ? prevState.filter((id) => id !== parentId)
        : [...prevState, parentId]
    );
  };

  // Filtrar los hijos por el id del padre
  const getFilteredHijos = (parentId) => {
    return rowsHijo.filter((hijo) => hijo[filterFieldHijo] === parentId);
  };

  // Función para aplicar text transform en el renderizado
  const applyTextTransform = (text, textTransform) => {
    switch (textTransform) {
      case "uppercase":
        return text.toUpperCase();
      case "lowercase":
        return text.toLowerCase();
      case "capitalize":
        return text
          .toLowerCase() // Asegura que todo el texto esté en minúsculas primero
          .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitaliza la primera letra de cada palabra
      case "numeric":
        // Si el texto es numérico, formatea el número con separadores de miles y decimales en el formato 'es-AR'
        let value = parseFloat(text) || 0; // Si es NaN, lo asignamos como 0
        // Si el valor es 0 o null, no mostrar nada
        if (value === 0 || value === null) {
          return <div></div>; // Retorna un div vacío si es 0 o null
        }
        // Formateamos el valor con separador de miles y 2 decimales
        const formattedValue = value.toLocaleString("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return formattedValue; // Si no es un número, devolvemos el texto original
      default:
        return text;
    }
  };

  // Estilos mejorados para la tabla
  const rowStyles = {
    "& .MuiTableRow-root:nth-of-type(odd)": {
      backgroundColor: "#e8eaf6", // Color más suave para filas impares
    },
    "& .MuiTableRow-root:nth-of-type(even)": {
      backgroundColor: "#f1f1f1", // Color más suave para filas pares
    },
    "& .MuiTableCell-root": {
      padding: "4px 12px", // Padding estándar
      margin: 0, // Eliminar márgenes
      borderBottom: "1px solid #ddd", // Borde sutil entre las celdas
    },
    "& .MuiTableRow-root": {
      borderBottom: "1px solid #ddd", // Borde entre las filas
      margin: 0, // Eliminar márgenes entre filas
    },
    // Estilos específicos para el encabezado
    "& .MuiTableHead-root .MuiTableCell-root": {
      fontWeight: "bold", // Negrita en el encabezado
      backgroundColor: "#e0e0e0", // Color de fondo del encabezado
      padding: "10px 12px", // Más padding en el encabezado para que sea más ancho
      borderBottom: "2px solid #bbb", // Borde más grueso para el encabezado
    },
    // Estilos para las filas padres (si las hay, por ejemplo, en una tabla colapsable)
    "& .MuiTableRow-root.parent-row": {
      backgroundColor: "#d0d0d0", // Un color más suave para las filas padres
    },
  };

  return (
    <TableContainer sx={rowStyles}>
      <Table>
        {/* Tabla de Padres */}
        <TableHead>
          <TableRow>
            {columnsPadre.map((col, index) => (
              <TableCell
                key={index}
                style={{
                  width: col.width || "auto",
                  textAlign: col.textTransform === "numeric" ? "right" : "left", // Alineación condicional
                }}
              >
                {col.headerName}
              </TableCell>
            ))}
            <TableCell style={{ textAlign: "center" }}>Detalle</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rowsPadre.map((padre) => {
            const isRowExpanded = expandedRows.includes(
              padre[filterFieldPadre]
            );
            return (
              <React.Fragment key={padre[filterFieldPadre]}>
                <TableRow>
                  {columnsPadre.map((col, index) => {
                    const cellValue = padre[col.field];
                    const transformedValue = col.textTransform
                      ? applyTextTransform(cellValue, col.textTransform)
                      : cellValue;

                    return (
                      <TableCell
                        key={index}
                        style={{
                          textAlign:
                            col.textTransform === "numeric" ? "right" : "left", // Alineación condicional
                          width: col.width || "auto",
                        }}
                      >
                        {col.renderCell
                          ? col.renderCell(transformedValue)
                          : transformedValue}
                      </TableCell>
                    );
                  })}
                  <TableCell style={{ textAlign: "center" }}>
                    <Button
                      onClick={() =>
                        handleToggleExpand(padre[filterFieldPadre])
                      }
                      style={{ padding: "4px 8px" }} // Si necesitas un botón más pequeño
                    >
                      {isRowExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={columnsPadre.length + 1}>
                    <Collapse in={isRowExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              {columnsHijo.map((col, index) => (
                                <TableCell
                                  key={index}
                                  style={{
                                    width: col.width || "auto",
                                    textAlign:
                                      col.textTransform === "numeric"
                                        ? "right"
                                        : "left", // Alineación condicional
                                  }}
                                >
                                  {col.headerName}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getFilteredHijos(padre[filterFieldPadre]).map(
                              (hijo, index) => (
                                <TableRow key={index}>
                                  {columnsHijo.map((col, idx) => {
                                    const cellValue = hijo[col.field];
                                    const transformedValue = col.textTransform
                                      ? applyTextTransform(
                                          cellValue,
                                          col.textTransform
                                        )
                                      : cellValue;

                                    return (
                                      <TableCell
                                        key={idx}
                                        style={{
                                          textAlign:
                                            col.textTransform === "numeric"
                                              ? "right"
                                              : "left", // Alineación condicional
                                          width: col.width || "auto",
                                        }}
                                      >
                                        {col.renderCell
                                          ? col.renderCell(transformedValue)
                                          : transformedValue}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CustomTableWithCollapse;
